/**
 * Utility functions for calculating questionnaire scores
 */

/**
 * Calculate the score for a questionnaire submission
 * @param {Object} formStructure - The form structure containing questions
 * @param {Object} responseData - The user's response data
 * @returns {Object} - Score details including points earned, total points, percentage, and details per question
 */
export const calculateQuestionnaireScore = (formStructure, responseData) => {
  if (!formStructure?.questions || !Array.isArray(formStructure.questions)) {
    return null;
  }

  // Filter out hidden questions (visible property is false)
  const visibleQuestions = formStructure.questions.filter(question => question.visible !== false);

  let totalPoints = 0;
  let earnedPoints = 0;
  const questionResults = [];

  visibleQuestions.forEach((question, index) => {
    const questionPoints = question.points || 1;
    totalPoints += questionPoints;

    const userAnswer = responseData[`question_${question.id}`] || responseData[question.id];
    const result = {
      questionNumber: index + 1,
      questionText: question.question,
      questionType: question.type,
      points: questionPoints,
      earnedPoints: 0,
      isCorrect: false,
      userAnswer: userAnswer,
      correctAnswer: null,
    };

    // Skip scoring for short answer questions (require manual grading)
    if (question.type === 'short_answer') {
      result.requiresManualGrading = true;
      result.earnedPoints = null; // Will be graded manually
      questionResults.push(result);
      return;
    }

    // Get correct answer(s)
    const correctOptions = question.options?.filter(opt => opt.isCorrect) || [];
    result.correctAnswer = correctOptions.map(opt => opt.text).join(', ');

    // Score based on question type
    switch (question.type) {
      case 'multiple_choice':
      case 'true_false':
        // Single correct answer
        const correctOption = correctOptions[0];
        if (correctOption && userAnswer === correctOption.text) {
          result.isCorrect = true;
          result.earnedPoints = questionPoints;
          earnedPoints += questionPoints;
        }
        break;

      case 'checkbox':
        // Multiple correct answers - all must be selected, no extras
        const correctAnswers = correctOptions.map(opt => opt.text).sort();
        const userAnswers = Array.isArray(userAnswer) 
          ? userAnswer.sort() 
          : (userAnswer ? [userAnswer] : []);

        if (JSON.stringify(correctAnswers) === JSON.stringify(userAnswers)) {
          result.isCorrect = true;
          result.earnedPoints = questionPoints;
          earnedPoints += questionPoints;
        } else {
          // Partial credit: give proportional points based on correct selections
          const correctSelections = userAnswers.filter(ans => 
            correctAnswers.includes(ans)
          ).length;
          const incorrectSelections = userAnswers.filter(ans => 
            !correctAnswers.includes(ans)
          ).length;
          
          if (correctSelections > 0 && incorrectSelections === 0) {
            // Partial credit only if no wrong answers selected
            const partialPoints = (correctSelections / correctAnswers.length) * questionPoints;
            result.earnedPoints = parseFloat(partialPoints.toFixed(2));
            earnedPoints += result.earnedPoints;
            result.isPartialCredit = true;
          }
        }
        break;

      default:
        break;
    }

    questionResults.push(result);
  });

  // Calculate final score
  const percentage = totalPoints > 0 ? (earnedPoints / totalPoints) * 100 : 0;
  const hasManualGrading = questionResults.some(q => q.requiresManualGrading);

  return {
    earnedPoints: parseFloat(earnedPoints.toFixed(2)),
    totalPoints,
    percentage: parseFloat(percentage.toFixed(2)),
    grade: getLetterGrade(percentage),
    remark: getRemark(percentage),
    passed: percentage >= 60, // Default pass threshold
    questionResults,
    hasManualGrading,
    timestamp: new Date().toISOString(),
  };
};

/**
 * Get letter grade based on percentage
 * @param {number} percentage - Score percentage
 * @returns {string} - Letter grade
 */
const getLetterGrade = (percentage) => {
  if (percentage >= 90) return 'A';
  if (percentage >= 80) return 'B';
  if (percentage >= 70) return 'C';
  if (percentage >= 60) return 'D';
  return 'F';
};

/**
 * Get remark/description based on percentage
 * @param {number} percentage - Score percentage
 * @returns {string} - Remark
 */
export const getRemark = (percentage) => {
  if (percentage >= 90) return 'Excellent';
  if (percentage >= 80) return 'Very Good';
  if (percentage >= 70) return 'Good';
  if (percentage >= 60) return 'Satisfactory';
  if (percentage >= 50) return 'Needs Improvement';
  return 'Poor';
};

/**
 * Format score for display
 * @param {Object} scoreData - Score calculation result
 * @returns {string} - Formatted score string
 */
export const formatScore = (scoreData) => {
  if (!scoreData) return 'N/A';
  
  if (scoreData.hasManualGrading) {
    return `${scoreData.earnedPoints}/${scoreData.totalPoints} (${scoreData.percentage.toFixed(1)}%) - Pending Manual Review`;
  }
  
  return `${scoreData.earnedPoints}/${scoreData.totalPoints} (${scoreData.percentage.toFixed(1)}%) - Grade: ${scoreData.grade}`;
};

/**
 * Check if a form is a questionnaire
 * @param {Object} form - Form object
 * @returns {boolean} - True if questionnaire
 */
export const isQuestionnaire = (form) => {
  return form?.form_structure?.isQuestionnaire === true;
};

